/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { SketchRelatedData } from '@tt4b/ttam-enums';
import { StateEnum, useAutoSaveSketchRelatedData, useStore } from '@tt4b/ttam-hooks';
import { i18n } from '@tt4b/ttam-i18n';
import {
  trackEvent,
  getEmptyCampaignDialogTag,
  setEmptyCampaignDialogTag,
  getReachLimitErrorDialogTag,
  doNotShowExitSystemDialog,
  getSaveSuccessExitDialogTag,
  setSaveSuccessExitDialogTag,
  logger,
  setReachLimitErrorDialogTag,
} from '@tt4b/ttam-utils';
import { Message } from 'byted-vui';
import { Ref, SetupContext, computed, h, ref, watch } from 'vue-demi';

export function useAutoSaveExitHandler(options: {
  campaignSketchRelatedData: Ref<SketchRelatedData>;
  adgroupSketchRelatedData: Ref<SketchRelatedData>;
  creativeSketchRelatedData: Ref<SketchRelatedData>;
  isSupportDraft: Ref<boolean>;
  emit: SetupContext['emit'];
}) {
  const { campaignSketchRelatedData, adgroupSketchRelatedData, creativeSketchRelatedData, isSupportDraft, emit } =
    options;
  const {
    isSketchReachLimit,
    isSketchSaving,
    isSketchSaved,
    isSketchSaveError,
    isSketchPermissionError,
    isSketchPublished,
    isSketchDeleted,
    isHasDraftId,
    getCodeAndMsg,
  } = useAutoSaveSketchRelatedData({
    campaignSketchRelatedData,
    adgroupSketchRelatedData,
    creativeSketchRelatedData,
  });

  const { state } = useStore();
  const aadvid = computed(() => state[StateEnum.baseInfo].value?.aadvid);
  const autoSaveExitButtonDisabled = ref(false);
  const emptyCampaignDialogVisible = ref(false);
  const exitReachLimitDialogVisible = ref(false);
  const isReachLimitDialogTriggeredByHeader = ref(false);
  const isReachLimitDialogTriggeredByHeaderConfirmExit = ref(false);
  const exitCommonErrorDialogVisible = ref(false);
  const isCommonErrorDialogTriggerByHeader = ref(false);
  const isCommonErrorDialogTriggerByHeaderConfirmExit = ref(false);

  const exitDialogVisible = ref(false);
  const isExitDialogTriggerByHeader = ref(false);
  const isExitDialogTriggerByHeaderConfirmExit = ref(false);

  // Empty Campaign Dialog 逻辑
  const handleEmptyCampaignDialogTrigger = () => {
    if (!getEmptyCampaignDialogTag(aadvid.value)) {
      emptyCampaignDialogVisible.value = true;
      trackEvent('ttam_creation_draft_exit_without_draft');
    } else {
      emit('goToListPage');
    }
  };

  const handleEmptyCampaignDialogClose = () => {
    emptyCampaignDialogVisible.value = false;
  };

  const handleEmptyCampaignDialogExit = (isNotShowAnymore: boolean) => {
    doNotShowExitSystemDialog();
    trackEvent('ttam_creation_draft_exit_without_draft_exit');
    if (isNotShowAnymore) {
      trackEvent('ttam_creation_draft_exit_without_draft_noshow');
      try {
        setEmptyCampaignDialogTag(aadvid.value);
      } catch (error) {
        logger.info('campaign is empty when auto save draft, localStorage error');
      }
    }
    emit('goToListPage');
  };

  // Exit Error 逻辑，包括 reach limit 和 common error
  const handleExitErrorDialogTrigger = () => {
    if (isSketchReachLimit.value) {
      if (!getReachLimitErrorDialogTag(aadvid.value)) {
        exitReachLimitDialogVisible.value = true;
        trackEvent('ttam_creation_draft_exit_on_error_upper_limit');
      } else {
        doNotShowExitSystemDialog();
        emit('goToListPage');
      }
    } else {
      exitCommonErrorDialogVisible.value = true;
      trackEvent('ttam_creation_draft_exit_on_error');
    }
  };

  // Reach Limit Dialog 逻辑
  const handleReachLimitDialogExit = (isNotShowAnymore: boolean) => {
    doNotShowExitSystemDialog();

    if (isNotShowAnymore) {
      setReachLimitErrorDialogTag(aadvid.value);
      trackEvent('ttam_creation_draft_exit_on_error_upper_limit_not_show_anymore');
    }

    // reach limit dialog 的触发分为两种情况：1.用户点击 Exit button; 2.用户点击 header menu（去该去的地方）
    if (isReachLimitDialogTriggeredByHeader.value) {
      isReachLimitDialogTriggeredByHeaderConfirmExit.value = true;
    } else {
      emit('goToListPage');
    }
  };

  const handleReachLimitDialogClose = () => {
    exitReachLimitDialogVisible.value = false;
  };

  // Common Error Dialog 逻辑
  const handleCommonErrorDialogExit = () => {
    doNotShowExitSystemDialog();
    // common dialog 的触发分为两种情况：1.用户点击 Exit button; 2.用户点击 header menu（去该去的地方）
    if (isCommonErrorDialogTriggerByHeader.value) {
      isCommonErrorDialogTriggerByHeaderConfirmExit.value = true;
    } else {
      emit('goToListPage');
    }
  };

  const handleCommonErrorDialogClose = () => {
    exitCommonErrorDialogVisible.value = false;
  };

  // exit dialog 逻辑，包括 save success 和 save error，不包括不支持 draft 的场景
  const handleExitDialogTrigger = () => {
    if (isSketchSaved.value) {
      if (getSaveSuccessExitDialogTag(aadvid.value)) {
        doNotShowExitSystemDialog();
        emit('goToListPage');
      } else {
        exitDialogVisible.value = true;
      }
    } else if (isSketchPermissionError.value) {
      doNotShowExitSystemDialog();
      emit('goToListPage');
    } else {
      exitDialogVisible.value = true;
      if (isSketchReachLimit.value) {
        return trackEvent('ttam_creation_draft_exit_on_error_upper_limit');
      }
      if (!isSketchDeleted.value && !isSketchPublished.value) {
        const { code, msg } = getCodeAndMsg();
        if (isHasDraftId.value) {
          trackEvent('ttam_creation_draft_saved_xxx_ago', { code, msg });
        } else {
          trackEvent('ttam_creation_no_draft_saved', { code, msg });
        }
      }
    }
  };

  const handleExitByClickHeaderMenuWhenNeedExitDialog = (resolve, reject) => {
    exitDialogVisible.value = true;
    isExitDialogTriggerByHeader.value = true;
    if (isSketchReachLimit.value) {
      trackEvent('ttam_creation_draft_exit_on_error_upper_limit');
    }
    const stopDialogVisibleWatch = watch(exitDialogVisible, () => {
      stopDialogVisibleWatch();
      // exitDialogVisible 为 false 有可能是 cancel，也有可能是点击了 exit
      isExitDialogTriggerByHeader.value = false;
      if (isExitDialogTriggerByHeaderConfirmExit.value) {
        resolve(true);
      } else {
        reject();
      }
    });
  };

  const handleExitByClickHeaderMenuWhenSaving = (resolve, reject) => {
    const loadingText = i18n.t('draft_auto_save') as string;
    const curMessage = Message({
      duration: 0,
      iconClass: 'vi-icon-ex-loading2 vi-icon-loading',
      message: h('div', {}, [
        h('span', { style: 'margin-left: 8px' }, loadingText),
        h(
          'a',
          {
            style: 'margin-left: 8px',
            on: {
              click: () => {
                trackEvent('ttam_creation_draft_exit_on_save_exit');
                resolve(true);
              },
            },
          },
          i18n.t('flexible_copy_footer_exit') as string,
        ),
      ]),
    });
    trackEvent('ttam_creation_draft_exit_on_save');
    const closeWatch = watch(isSketchSaving, () => {
      curMessage.close();
      closeWatch();
      handleExitDialogTrigger();
    });
  };

  const handleExitDialogClose = () => {
    exitDialogVisible.value = false;
  };

  const handleExitDialogExit = (isDraftSavedNotShowAnymore: boolean) => {
    doNotShowExitSystemDialog();
    if (isDraftSavedNotShowAnymore) {
      setSaveSuccessExitDialogTag(aadvid.value);
    }
    // 触发分为两种情况：1.用户点击 Exit button; 2.用户点击 header menu（去该去的地方）
    if (isExitDialogTriggerByHeader.value) {
      isExitDialogTriggerByHeaderConfirmExit.value = true;
    } else {
      emit('goToListPage');
    }
  };

  // 处理用户点击 header 上的 menu 退出创编流的情况
  const handleExitByClickHeaderMenu: () => Promise<boolean> = () =>
    new Promise((resolve, reject) => {
      if (!isSupportDraft.value) {
        handleExitByClickHeaderMenuWhenNeedExitDialog(resolve, reject);
      } else if (isSketchSaving.value) {
        handleExitByClickHeaderMenuWhenSaving(resolve, reject);
      } else if (isSketchSaved.value) {
        if (getSaveSuccessExitDialogTag(aadvid.value)) {
          return resolve(true);
        } else {
          handleExitByClickHeaderMenuWhenNeedExitDialog(resolve, reject);
        }
      } else if (isSketchSaveError.value) {
        if (isSketchPermissionError.value) {
          doNotShowExitSystemDialog();
          return resolve(true);
        } else {
          handleExitByClickHeaderMenuWhenNeedExitDialog(resolve, reject);
        }
      } else {
        return resolve(true);
      }
    });

  // 处理用户点击 exit 按钮退出创编流的情况
  const handleExitByClickExitButton = () => {
    trackEvent('ttam_creation_exit');
    if (!isSupportDraft.value) {
      exitDialogVisible.value = true;
      return;
    }
    if (isSketchSaving.value) {
      autoSaveExitButtonDisabled.value = isSketchSaving.value;
      const stopDialogStatusWatch = watch(isSketchSaving, () => {
        stopDialogStatusWatch();
        handleExitDialogTrigger();
        autoSaveExitButtonDisabled.value = false;
      });
      return;
    }
    handleExitDialogTrigger();
  };

  // 处理用户点击 exit button 旁边的 discard
  const handleExitByClickDiscard = () => {
    doNotShowExitSystemDialog();
    emit('discard');
  };

  const isShowDiscardDropDown = computed(() => isSupportDraft.value && !isSketchPermissionError.value);

  return {
    autoSaveExitButtonDisabled,
    emptyCampaignDialogVisible,
    exitReachLimitDialogVisible,
    isReachLimitDialogTriggeredByHeader,
    isReachLimitDialogTriggeredByHeaderConfirmExit,
    exitCommonErrorDialogVisible,
    isCommonErrorDialogTriggerByHeader,
    isCommonErrorDialogTriggerByHeaderConfirmExit,
    // 目前在用的仅有下面的
    exitDialogVisible,
    isExitDialogTriggerByHeader,
    isExitDialogTriggerByHeaderConfirmExit,
    isShowDiscardDropDown,

    handleEmptyCampaignDialogTrigger,
    handleEmptyCampaignDialogClose,
    handleEmptyCampaignDialogExit,

    handleExitErrorDialogTrigger,
    handleReachLimitDialogExit,
    handleReachLimitDialogClose,
    handleCommonErrorDialogExit,
    handleCommonErrorDialogClose,

    handleExitDialogTrigger,
    handleExitDialogExit,
    handleExitDialogClose,

    handleExitByClickExitButton,
    handleExitByClickHeaderMenu,
    handleExitByClickDiscard,
  };
}
